import React, { useMemo, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import * as XLSX from 'xlsx';
import { useData } from '../../context/DataContext';
import { useAuth } from '../../context/AuthContext';
import { calculateWeightedFinalGrade, floorToOneDecimal, formatGradeOneDecimal, normalizeExamType } from '../../utils/gradeCalculation';
import logoWide from '../../assets/logo_wide.png';
import './Reports.css';
import './AnnualGradeList.css';

const PASS_GRADE = 5.5;

const idOf = (value) => String(value?.id || value?._id || value || '');

const escapeHtml = (value = '') => String(value ?? '')
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;');

// dateOnly: geboortedatums staan als UTC-middernacht opgeslagen; zonder UTC verschuift de dag in Suriname.
const formatLongDate = (value, { dateOnly = false } = {}) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleDateString('nl-NL', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    ...(dateOnly ? { timeZone: 'UTC' } : {})
  });
};

const getExamPoints = (course) => (course?.examPoints || []).map((point, index) => {
  const examType = normalizeExamType(point.name || 'other');
  return {
    ...point,
    examType,
    pointKey: String(point._id || point.id || `${examType}-${index}`)
  };
});

// Zelfde zoeklogica als bij "Cijfers invullen": eerst op examenonderdeel, anders op toetsvorm.
const findPointGrade = (courseGrades, point) => {
  const byKey = courseGrades.find((g) => String(g.examPointKey || '') === point.pointKey);
  if (byKey) return byKey.grade;
  const byType = courseGrades.find((g) => normalizeExamType(g.examType || g.notes || 'other') === point.examType);
  return byType ? byType.grade : null;
};

const averageOf = (entries) => {
  const valid = entries.filter((entry) => typeof entry.value === 'number');
  if (!valid.length) return null;
  const useWeights = valid.every((entry) => entry.weight > 0);
  const totalWeight = valid.reduce((sum, entry) => sum + (useWeights ? entry.weight : 1), 0);
  return valid.reduce((sum, entry) => sum + entry.value * (useWeights ? entry.weight : 1), 0) / totalWeight;
};

const AnnualGradeList = () => {
  const { selectedLocation } = useOutletContext();
  const { user } = useAuth();
  const { data } = useData();

  const [selectedStudy, setSelectedStudy] = useState('');
  const [selectedStudyYear, setSelectedStudyYear] = useState('');
  const [selectedStudent, setSelectedStudent] = useState('');
  const [studentSearch, setStudentSearch] = useState('');
  const [selectedCohort, setSelectedCohort] = useState('');
  const [studentCodes, setStudentCodes] = useState({});

  const courseById = useMemo(() => {
    const map = new Map();
    (data.courses || []).forEach((course) => map.set(idOf(course), course));
    return map;
  }, [data.courses]);

  const gradesByStudentCourse = useMemo(() => {
    const map = new Map();
    (data.grades || []).forEach((grade) => {
      const key = `${idOf(grade.studentId)}|${idOf(grade.courseId)}`;
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(grade);
    });
    return map;
  }, [data.grades]);

  const studies = useMemo(
    () => [...(data.studies || [])].sort((a, b) => (a.name || '').localeCompare(b.name || '', 'nl')),
    [data.studies]
  );

  const study = studies.find((s) => idOf(s) === String(selectedStudy));
  const studyYears = useMemo(
    () => (study?.studyYears || []).slice().sort((a, b) => Number(a.yearNumber || 0) - Number(b.yearNumber || 0)),
    [study]
  );
  const studyYear = studyYears.find((y) => idOf(y) === String(selectedStudyYear));

  const getModuleCourses = (module) => (module.courses || []).map((course) => courseById.get(idOf(course)) || course);

  const yearCourseIds = useMemo(() => {
    const ids = new Set();
    (studyYear?.modules || []).forEach((module) => getModuleCourses(module).forEach((course) => ids.add(idOf(course))));
    return ids;
  }, [studyYear, courseById]); // eslint-disable-line react-hooks/exhaustive-deps

  // Studenten die in dit studiejaar ingeschreven zijn voor een vak of er een cijfer voor hebben.
  const yearStudents = useMemo(() => {
    if (!studyYear) return [];
    const studentIds = new Set();
    (data.enrollments || []).forEach((enrollment) => {
      if (yearCourseIds.has(idOf(enrollment.courseId))) studentIds.add(idOf(enrollment.studentId));
    });
    (data.grades || []).forEach((grade) => {
      if (yearCourseIds.has(idOf(grade.courseId))) studentIds.add(idOf(grade.studentId));
    });

    return (data.students || [])
      .filter((student) => studentIds.has(idOf(student)))
      .filter((student) => !selectedCohort || idOf(student.cohort) === String(selectedCohort))
      .filter((student) => {
        const studentLocation = String(student.location || '').toLowerCase();
        if (user.role === 'manager' && user.location) return studentLocation === String(user.location).toLowerCase();
        if (user.role === 'admin' && selectedLocation && selectedLocation !== 'all') return studentLocation === String(selectedLocation).toLowerCase();
        return true;
      })
      .sort((a, b) => (a.name || '').localeCompare(b.name || '', 'nl'));
  }, [data.enrollments, data.grades, data.students, selectedCohort, selectedLocation, studyYear, user, yearCourseIds]);

  const buildModuleResult = (module, studentId) => {
    const courseResults = getModuleCourses(module).map((course) => {
      const courseGrades = gradesByStudentCourse.get(`${studentId}|${idOf(course)}`) || [];
      const examPoints = getExamPoints(course);
      const weighted = calculateWeightedFinalGrade({ grades: courseGrades, examPoints });
      const rows = examPoints.length > 0
        ? examPoints.map((point) => ({ toetsing: point.name, grade: findPointGrade(courseGrades, point) }))
        : [{ toetsing: courseGrades[0]?.notes || '', grade: typeof weighted.finalGrade === 'number' ? weighted.finalGrade : null }];
      return {
        code: course.code || '',
        name: course.name || '',
        ects: Number(course.ects || 0),
        finalGrade: typeof weighted.finalGrade === 'number' ? weighted.finalGrade : null,
        isComplete: weighted.isComplete,
        rows
      };
    });

    const finalGrade = averageOf(courseResults.map((c) => ({ value: c.finalGrade, weight: c.ects })));
    const flooredFinal = floorToOneDecimal(finalGrade);
    const moduleEcts = Number(module.ects || 0) || courseResults.reduce((sum, c) => sum + c.ects, 0);
    const allGraded = courseResults.length > 0 && courseResults.every((c) => typeof c.finalGrade === 'number');

    return {
      code: module.code || '',
      name: module.name || '',
      ects: moduleEcts,
      finalGrade,
      passed: allGraded && typeof flooredFinal === 'number' && flooredFinal >= PASS_GRADE,
      courses: courseResults
    };
  };

  const buildSheet = (student) => {
    const studentId = idOf(student);
    const modules = (studyYear?.modules || []).map((module) => buildModuleResult(module, studentId));

    // Voortgang wordt over de hele studie berekend (alle studiejaren).
    let totalEcts = 0;
    let earnedEcts = 0;
    studyYears.forEach((year) => {
      (year.modules || []).forEach((module) => {
        const result = buildModuleResult(module, studentId);
        totalEcts += result.ects;
        if (result.passed) earnedEcts += result.ects;
      });
    });

    const yearEctsEarned = modules.filter((m) => m.passed).reduce((sum, m) => sum + m.ects, 0);
    const yearAverage = averageOf(modules.map((m) => ({ value: m.finalGrade, weight: m.ects })));

    return {
      student,
      studentCode: studentCodes[studentId] || '',
      yearLabel: `LEERJAAR ${studyYear?.yearNumber || ''}`.trim(),
      yearName: studyYear?.name || '',
      studyName: study?.name || '',
      modules,
      totalEcts,
      earnedEcts,
      progress: totalEcts > 0 ? earnedEcts / totalEcts : 0,
      yearEctsEarned,
      yearEctsTotal: modules.reduce((sum, m) => sum + m.ects, 0),
      yearAverage
    };
  };

  const overview = useMemo(
    () => yearStudents.map((student) => buildSheet(student)),
    [yearStudents, studentCodes, gradesByStudentCourse, studyYear, studyYears] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const filteredOverview = overview.filter((sheet) => {
    const query = studentSearch.trim().toLowerCase();
    if (!query) return true;
    return `${sheet.student.name || ''} ${sheet.student.email || ''}`.toLowerCase().includes(query);
  });

  const selectedSheet = overview.find((sheet) => idOf(sheet.student) === String(selectedStudent));

  const formatGrade = (value) => (typeof value === 'number' ? formatGradeOneDecimal(value) : '');

  const renderSheetHtml = (sheet) => {
    const logoUrl = new URL(logoWide, window.location.origin).href;
    const moduleRows = sheet.modules.map((module) => {
      const courseRows = module.courses.map((course) => course.rows.map((row, index) => `
        <tr>
          <td>${index === 0 && course.code ? `(${escapeHtml(course.code)})` : ''}</td>
          <td>${index === 0 ? escapeHtml(course.name) : ''}</td>
          <td></td>
          <td>${escapeHtml(row.toetsing)}</td>
          <td class="agl-num">${formatGrade(row.grade)}</td>
          <td></td>
        </tr>`).join('')).join('');
      return `
        <tr class="agl-module-row">
          <td>${escapeHtml(module.code)}</td>
          <td>${escapeHtml(module.name)}</td>
          <td class="agl-num">${module.ects || ''}</td>
          <td></td>
          <td></td>
          <td class="agl-num ${module.finalGrade !== null && !module.passed ? 'agl-fail' : ''}">${formatGrade(module.finalGrade)}</td>
        </tr>
        ${courseRows}
        <tr class="agl-spacer"><td colspan="6"></td></tr>`;
    }).join('');

    return `
      <div class="agl-sheet">
        <div class="agl-logo"><img src="${logoUrl}" alt="Netherlands Business Academy" /></div>
        <div class="agl-year">${escapeHtml(sheet.yearLabel)}</div>
        <div class="agl-study">${escapeHtml(sheet.studyName)}${sheet.yearName ? ` · ${escapeHtml(sheet.yearName)}` : ''}</div>
        <table class="agl-info">
          <tr><td class="agl-lbl">Naam:</td><td>${escapeHtml(sheet.student.name)}</td><td class="agl-lbl">Totaal te behalen ECTS</td><td class="agl-num">${sheet.totalEcts}</td></tr>
          <tr><td class="agl-lbl">Studentcode:</td><td>${escapeHtml(sheet.studentCode || '-')}</td><td class="agl-lbl">ECTS behaald</td><td class="agl-num">${sheet.earnedEcts}</td></tr>
          <tr><td class="agl-lbl">Geboortedatum:</td><td>${escapeHtml(formatLongDate(sheet.student.dateOfBirth, { dateOnly: true }))}</td><td class="agl-lbl">Voortgang studie</td><td class="agl-num">${Math.round(sheet.progress * 100)}%</td></tr>
        </table>
        <table class="agl-grades">
          <thead>
            <tr><th>CODE</th><th>MODULE NAAM</th><th>ECTS</th><th>TOETSING</th><th>CIJFER</th><th>EINDCIJFER</th></tr>
          </thead>
          <tbody>
            ${moduleRows || '<tr><td colspan="6">Geen modules gekoppeld aan dit studiejaar.</td></tr>'}
          </tbody>
        </table>
        <div class="agl-signatures">
          <div><div class="agl-sig-line"></div><strong>R. S. Soekhai BBA</strong><br/>Directeur<br/>Netherlands Business Academy<br/>Suriname</div>
          <div><div class="agl-sig-line"></div><strong>E. Audhoe MBA</strong><br/>Examencommissie<br/>Netherlands Business Academy<br/>Suriname</div>
        </div>
        <div class="agl-printed">Afgedrukt op ${escapeHtml(formatLongDate(new Date()))}</div>
      </div>`;
  };

  const SHEET_STYLES = `
    .agl-sheet { font-family: Helvetica, Arial, sans-serif; color: #000; font-size: 10pt; background: #fff; }
    .agl-sheet .agl-logo img { width: 70%; max-width: 420px; display: block; margin: 0 auto 10px; }
    .agl-sheet .agl-year { text-align: center; font-weight: 700; font-size: 14pt; margin: 6px 0 2px; }
    .agl-sheet .agl-study { text-align: center; font-size: 10pt; color: #374151; margin-bottom: 10px; }
    .agl-sheet .agl-info { width: 100%; border-collapse: collapse; margin-bottom: 10px; }
    .agl-sheet .agl-info td { padding: 2px 6px; }
    .agl-sheet .agl-lbl { font-weight: 700; white-space: nowrap; }
    .agl-sheet .agl-grades { width: 100%; border-collapse: collapse; }
    .agl-sheet .agl-grades th { background: #6b1d6e; color: #fff; text-align: left; padding: 4px 6px; font-size: 9pt; }
    .agl-sheet .agl-grades td { padding: 2px 6px; border-bottom: 1px solid #e5e7eb; font-size: 9pt; }
    .agl-sheet .agl-module-row td { font-weight: 700; background: #f3e8f4; border-top: 1px solid #6b1d6e; }
    .agl-sheet .agl-spacer td { border: 0; height: 4px; padding: 0; }
    .agl-sheet .agl-num { text-align: right; }
    .agl-sheet .agl-fail { color: #b91c1c; }
    .agl-sheet .agl-signatures { display: flex; justify-content: space-between; gap: 24px; margin-top: 28px; font-size: 9.5pt; }
    .agl-sheet .agl-signatures > div { width: 45%; }
    .agl-sheet .agl-sig-line { height: 40px; }
    .agl-sheet .agl-printed { margin-top: 14px; font-size: 8pt; color: #6b7280; }
  `;

  const PRINT_PAGE_STYLES = `
    @page { size: A4; margin: 12mm 14mm; }
    body { margin: 0; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    .agl-sheet { page-break-after: always; }
    .agl-sheet:last-child { page-break-after: auto; }
    .agl-sheet * { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  `;

  const printSheets = (sheets) => {
    if (!sheets.length) return;
    const html = `<!doctype html><html lang="nl"><head><meta charset="utf-8" /><title>Cijferlijst</title><style>${SHEET_STYLES}${PRINT_PAGE_STYLES}</style></head><body>${sheets.map(renderSheetHtml).join('')}</body></html>`;
    const iframe = document.createElement('iframe');
    iframe.style.position = 'fixed';
    iframe.style.width = '0';
    iframe.style.height = '0';
    iframe.style.border = '0';
    document.body.appendChild(iframe);
    const doc = iframe.contentWindow.document;
    doc.open();
    doc.write(html);
    doc.close();
    const images = Array.from(doc.images);
    Promise.all(images.map((img) => (img.complete ? null : new Promise((resolve) => { img.onload = resolve; img.onerror = resolve; }))))
      .then(() => {
        iframe.contentWindow.focus();
        iframe.contentWindow.print();
        setTimeout(() => document.body.removeChild(iframe), 60000);
      });
  };

  const exportSheetExcel = (sheet) => {
    const rows = [
      ['Netherlands Business Academy'],
      [sheet.yearLabel, sheet.studyName],
      ['Naam:', sheet.student.name, 'Totaal te behalen ECTS', '', '', sheet.totalEcts],
      ['Studentcode:', sheet.studentCode, 'ECTS behaald', '', '', sheet.earnedEcts],
      ['Geboortedatum:', formatLongDate(sheet.student.dateOfBirth, { dateOnly: true }), 'Voortgang studie', '', '', Number(sheet.progress.toFixed(2))],
      [],
      ['CODE', 'MODULE NAAM', 'ECTS', 'TOETSING', 'CIJFER', 'EINDCIJFER']
    ];
    sheet.modules.forEach((module) => {
      rows.push([module.code, module.name, module.ects || '', '', '', module.finalGrade !== null ? floorToOneDecimal(module.finalGrade) : '']);
      module.courses.forEach((course) => {
        course.rows.forEach((row, index) => {
          rows.push([
            index === 0 && course.code ? `(${course.code})` : '',
            index === 0 ? course.name : '',
            '',
            row.toetsing || '',
            typeof row.grade === 'number' ? floorToOneDecimal(row.grade) : '',
            ''
          ]);
        });
      });
      rows.push([]);
    });
    rows.push([]);
    rows.push(['R. S. Soekhai BBA', '', '', 'E. Audhoe MBA']);
    rows.push(['Directeur', '', '', 'Examencommissie']);
    rows.push(['Netherlands Business Academy', '', '', 'Netherlands Business Academy']);
    rows.push(['Suriname', '', '', 'Suriname']);

    const worksheet = XLSX.utils.aoa_to_sheet(rows);
    worksheet['!cols'] = [{ wch: 16 }, { wch: 48 }, { wch: 8 }, { wch: 28 }, { wch: 10 }, { wch: 12 }];
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, sheet.yearLabel.slice(0, 31) || 'Cijferlijst');
    const safeName = String(sheet.student.name || 'student').replace(/[^\w-]+/g, '_');
    XLSX.writeFile(workbook, `Cijferlijst_${safeName}_${sheet.yearLabel.replace(/\s+/g, '_')}.xlsx`);
  };

  // Cohorten van de gekozen studie (val terug op alle cohorten als de studie er geen heeft gekoppeld).
  const cohortOptions = (study?.cohorts?.length ? study.cohorts : (data.cohorts || []))
    .filter((cohort) => user.role !== 'manager' || !user.location || String(cohort.location || '').toLowerCase() === String(user.location).toLowerCase());

  const resetBelowStudy = () => {
    setSelectedStudent('');
    setStudentSearch('');
  };

  return (
    <div className="reports-page">
      <div className="page-header">
        <h1>Jaarlijkse cijferlijst</h1>
      </div>
      <p className="hint-text" style={{ marginTop: 0 }}>
        Cijferlijst per studiejaar en student, gegroepeerd per module. Cijfers worden ingevoerd via "Cijfers invullen".
      </p>

      <div className="report-controls">
        <div className="form-group">
          <label>Studie</label>
          <select
            value={selectedStudy}
            onChange={(e) => {
              setSelectedStudy(e.target.value);
              setSelectedStudyYear('');
              setSelectedCohort('');
              resetBelowStudy();
            }}
          >
            <option value="">-- Kies studie --</option>
            {studies.map((s) => (
              <option key={idOf(s)} value={idOf(s)}>{s.name}</option>
            ))}
          </select>
        </div>

        <div className="form-group">
          <label>Studiejaar</label>
          <select
            value={selectedStudyYear}
            onChange={(e) => {
              setSelectedStudyYear(e.target.value);
              resetBelowStudy();
            }}
            disabled={!selectedStudy}
          >
            <option value="">-- Kies studiejaar --</option>
            {studyYears.map((year) => (
              <option key={idOf(year)} value={idOf(year)}>
                {year.name || `Studiejaar ${year.yearNumber || ''}`}
              </option>
            ))}
          </select>
        </div>

        <div className="form-group">
          <label>Cohort</label>
          <select
            value={selectedCohort}
            onChange={(e) => {
              setSelectedCohort(e.target.value);
              resetBelowStudy();
            }}
            disabled={!selectedStudy}
          >
            <option value="">-- Alle cohorten --</option>
            {cohortOptions.map((cohort) => (
              <option key={idOf(cohort)} value={idOf(cohort)}>{cohort.name || cohort.code}</option>
            ))}
          </select>
        </div>

        <div className="form-group">
          <label>Student zoeken</label>
          <input
            type="search"
            placeholder="Naam of e-mail..."
            value={studentSearch}
            onChange={(e) => setStudentSearch(e.target.value)}
            disabled={!studyYear}
          />
        </div>

        <div className="form-group">
          <label>Student</label>
          <select value={selectedStudent} onChange={(e) => setSelectedStudent(e.target.value)} disabled={!studyYear}>
            <option value="">-- Kies student ({filteredOverview.length}) --</option>
            {filteredOverview.map((sheet) => (
              <option key={idOf(sheet.student)} value={idOf(sheet.student)}>{sheet.student.name}</option>
            ))}
          </select>
        </div>
      </div>

      {studyYear && (
        <div className="export-buttons">
          <button className="btn-primary" onClick={() => printSheets([selectedSheet])} disabled={!selectedSheet}>
            Printen / PDF (student)
          </button>
          <button className="btn-primary" onClick={() => exportSheetExcel(selectedSheet)} disabled={!selectedSheet}>
            Excel (student)
          </button>
          <button className="btn-secondary" onClick={() => printSheets(filteredOverview)} disabled={!filteredOverview.length}>
            Alle studenten printen ({filteredOverview.length})
          </button>
        </div>
      )}

      {studyYear && !selectedSheet && (
        <div className="report-preview">
          <h3>Overzicht {studyYear.name || ''}</h3>
          {filteredOverview.length === 0 ? (
            <p>Geen studenten met inschrijvingen of cijfers in dit studiejaar.</p>
          ) : (
            <table className="data-table">
              <thead>
                <tr>
                  <th>Student</th>
                  <th>E-mail</th>
                  <th>Gemiddelde</th>
                  <th>ECTS dit jaar</th>
                  <th>Voortgang studie</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {filteredOverview.map((sheet) => (
                  <tr key={idOf(sheet.student)}>
                    <td>{sheet.student.name}</td>
                    <td>{sheet.student.email}</td>
                    <td>{typeof sheet.yearAverage === 'number' ? formatGradeOneDecimal(sheet.yearAverage) : '-'}</td>
                    <td>{sheet.yearEctsEarned} / {sheet.yearEctsTotal}</td>
                    <td>{Math.round(sheet.progress * 100)}%</td>
                    <td>
                      <button type="button" className="btn-secondary" onClick={() => setSelectedStudent(idOf(sheet.student))}>
                        Cijferlijst
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {selectedSheet && (
        <div className="report-preview">
          <div className="agl-toolbar">
            <button type="button" className="btn-secondary" onClick={() => setSelectedStudent('')}>← Terug naar overzicht</button>
            <label>
              Studentcode
              <input
                type="text"
                value={studentCodes[idOf(selectedSheet.student)] || ''}
                onChange={(e) => setStudentCodes((prev) => ({ ...prev, [idOf(selectedSheet.student)]: e.target.value }))}
                placeholder="bijv. B-SR-2024-040"
              />
            </label>
          </div>
          <style>{SHEET_STYLES}</style>
          <div className="agl-preview" dangerouslySetInnerHTML={{ __html: renderSheetHtml(selectedSheet) }} />
        </div>
      )}
    </div>
  );
};

export default AnnualGradeList;
